import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "./config/firebase-config.js";

/**
 *  Database Structure
 * users (Collection)
 *   ├── userId_1 (Document)
 *   │     ├── categories (Subcollection)
 *   │     │     ├── categoryId_1 (Document)
 *   │     │     │     ├── name: "Math"
 *   │     │     │     ├── studyMaterial: "Algebra notes"
 *   │     │     │     ├── questions (Subcollection)
 *   │     │     │     │     ├── questionId_1 (Document)
 *   │     │     │     │     │     ├── answer, description, type, etc.
 *   │     │     │     │     ├── questionId_2 (Document)
 *   │     │     │     │     ├── ...
 */

function getCurrentUser() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error("No user is authenticated.");
  }
  return user.uid;
}

// ✅ Function to add a category under the authenticated user
async function addCategory(categoryName, studyMaterial) {
  try {
    // Reference the categories subcollection for this user
    const userId = getCurrentUser();
    const categoriesCollectionRef = collection(
      db,
      "users",
      userId,
      "categories"
    );
    const docRef = await addDoc(categoriesCollectionRef, {
      name: categoryName,
      material: studyMaterial,
    });
    // console.log("Category successfully added with ID: ", docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("Error adding category", error);
  }
}

// ✅ Function to get all categories for the authenticated user
async function getCategories() {
  try {
    const userId = getCurrentUser();
    const categoriesCollectionRef = collection(
      db,
      "users",
      userId,
      "categories"
    );
    const querySnapshot = await getDocs(categoriesCollectionRef);
    const categories = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    // console.log(" Fetched Categories from Firestore:", categories);
    return categories;
  } catch (error) {
    console.error(" Error fetching categories:", error);
    return [];
  }
}

// ✅ Function to get number of questions in a specific category
async function getQuestionCountByCategory(categoryId) {
  try {
    const userId = getCurrentUser();
    const questionsCollectionRef = collection(
      db,
      "users",
      userId,
      "categories",
      categoryId,
      "questions"
    );
    const snapshot = await getDocs(questionsCollectionRef);
    return snapshot.size; // Number of documents (questions)
  } catch (error) {
    console.error(
      `❌ Error fetching question count for category ${categoryId}:`,
      error
    );
    return 0;
  }
}

// ✅ Function to get all questions from a specific category
async function getQuestions(categoryId) {
  try {
    // console.log(" Fetching questions for categoryId:", categoryId);
    if (!categoryId) {
      throw new Error(" categoryId is undefined or empty.");
    }
    // Reference the questions subcollection
    const userId = getCurrentUser();
    const questionsCollectionRef = collection(
      db,
      "users",
      userId,
      "categories",
      categoryId,
      "questions"
    );
    // console.log("✅ Firestore Path:", questionsCollectionRef.path);
    // Fetch all questions
    const querySnapshot = await getDocs(questionsCollectionRef);
    if (querySnapshot.empty) {
      console.warn(
        " No questions found in Firestore for category:",
        categoryId
      );
      return [];
    }
    const questions = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    // console.log(" Fetched Questions from Firestore:", questions);
    return questions;
  } catch (error) {
    console.error(" Error getting questions:", error);
    return [];
  }
}

// ✅ Function to get study material for a specific category
async function getMaterial(categoryId) {
  try {
    // console.log("Fetching material for categoryId:", categoryId);
    if (!categoryId) {
      throw new Error("בategoryId is undefined or empty.");
    }
    // Reference the correct Firestore path
    const userId = getCurrentUser();
    const categoryDocRef = doc(db, "users", userId, "categories", categoryId); // ✅ Using correct categoryId
    // console.log(" Firestore docRef path:", categoryDocRef.path);
    // Fetch document
    const categoryDoc = await getDoc(categoryDocRef);
    if (!categoryDoc.exists()) {
      throw new Error(` Category "${categoryId}" does not exist.`);
    }
    // console.log(" Firestore Document Data:", categoryDoc.data());
    // ✅ Retrieve `material` field
    const material = categoryDoc.data().material || "אין חומר לימוד";
    // console.log(" Fetched Study Material:", material);
    return material;
  } catch (error) {
    console.error(" Error fetching study material:", error.message);
    return "לא נמצא חומר לימוד";
  }
}

async function getCategoryById(categoryId) {
  try {
    // console.log(" Fetching category name for categoryId:", categoryId);
    if (!categoryId) {
      throw new Error(" categoryId is undefined or empty.");
    }
    const userId = getCurrentUser();
    const categoryDocRef = doc(db, "users", userId, "categories", categoryId);
    const categoryDoc = await getDoc(categoryDocRef);
    if (!categoryDoc.exists()) {
      throw new Error(` Category "${categoryId}" does not exist.`);
    }
    const categoryData = categoryDoc.data();
    // console.log(" Category Data:", categoryData);
    return categoryData;
  } catch (error) {
    console.error(" Error fetching category:", error);
    return { name: "קטגוריה לא נמצאה" }; // Default name if error occurs
  }
}

async function saveQuestion(
  categoryId,
  questionId,
  questionData,
  oldCategoryId = null,
  oldQuestionId = null
) {
  try {
    if (!categoryId) {
      throw new Error("❌ Missing categoryId");
    }
    let questionDocRef;
    let isEditing = false;
    if (questionId) {
      const userId = getCurrentUser();
      questionDocRef = doc(
        db,
        "users",
        userId,
        "categories",
        categoryId,
        "questions",
        questionId
      );
      const questionSnap = await getDoc(questionDocRef);
      if (questionSnap.exists()) {
        isEditing = true;
      }
    }
    if (isEditing) {
      await updateDoc(questionDocRef, questionData); // ✅ Update existing question
      // console.log("✅ Question updated successfully:", questionData);
    } else {
      const userId = getCurrentUser();
      questionDocRef = await addDoc(
        collection(db, "users", userId, "categories", categoryId, "questions"),
        questionData
      );
      // console.log("✅ New Question Created with ID:", questionDocRef.id);
      questionId = questionDocRef.id;
    }
    if (oldCategoryId && oldCategoryId !== categoryId) {
      const userId = getCurrentUser();
      const oldQuestionRef = doc(
        db,
        "users",
        userId,
        "categories",
        oldCategoryId,
        "questions",
        oldQuestionId
      );
      try {
        await deleteDoc(oldQuestionRef);
      } catch (error) {
        console.error(
          `❌ Error deleting question from ${oldCategoryId}:`,
          error
        );
      }
    }
    return questionId; // ✅ Return the document ID (either existing or new)
  } catch (error) {
    console.error("❌ Error saving question:", error);
    throw error;
  }
}

async function updateMaterial(categoryId, newMaterial) {
  const userId = getCurrentUser();
  const docRef = doc(db, "users", userId, "categories", categoryId);
  await updateDoc(docRef, { material: newMaterial });
}

const updateCategoryName = async (categoryId, newName) => {
  if (!categoryId || !newName) {
    throw new Error("Invalid category ID or name.");
  }
  const userId = getCurrentUser();
  const categoryRef = doc(db, "users", userId, "categories", categoryId);
  const categorySnap = await getDoc(categoryRef);
  if (!categorySnap.exists()) {
    throw new Error("Category does not exist.");
  }
  await updateDoc(categoryRef, {
    name: newName,
  });
};

const deleteCategory = async (categoryId) => {
  const userId = getCurrentUser();
  const categoryRef = doc(db, "users", userId, "categories", categoryId);
  const questionsRef = collection(
    db,
    "users",
    userId,
    "categories",
    categoryId,
    "questions"
  );
  try {
    // 1. Delete all questions in the subcollection
    const questionsSnapshot = await getDocs(questionsRef);
    const deletePromises = questionsSnapshot.docs.map((doc) =>
      deleteDoc(doc.ref)
    );
    await Promise.all(deletePromises);

    // 2. Delete the category document itself
    await deleteDoc(categoryRef);

    // console.log(`✅ Category "${categoryId}" and its questions were deleted.`);
  } catch (error) {
    console.error("❌ Error deleting category:", error);
    throw error;
  }
};

const deleteQuestion = async (categoryId, questionId) => {
  if (!categoryId || !questionId) {
    throw new Error("Missing categoryId or questionId.");
  }
  const userId = getCurrentUser();
  const questionRef = doc(
    db,
    "users",
    userId,
    "categories",
    categoryId,
    "questions",
    questionId
  );

  try {
    await deleteDoc(questionRef);
    // console.log(`✅ Question "${questionId}" deleted from category "${categoryId}".`);
  } catch (error) {
    console.error("❌ Error deleting question:", error);
    throw error;
  }
};

// ✅ Export functions
export {
  updateMaterial,
  updateCategoryName,
  deleteCategory,
  deleteQuestion,
  saveQuestion,
  getCategoryById,
  addCategory,
  getCategories,
  getQuestions,
  getMaterial,
  getQuestionCountByCategory,
};
